/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useState, useEffect} from "react";

import {useDispatch} from "react-redux";
import useTypedSelector from "@Services/Hooks/UseTypedSelector";

import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";
import CHANGE_API_REQUEST_TOKEN_ACTION from "@Actions/Api/ChangeApiRequestTokenValue";


import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";


import ApplicationApiContext from "@Contexts/Api";


import {Redirect, Route, Switch} from "react-router-dom";


import ApplicationHeaderComponent from "@UIParts/Header";
import ApplicationNotificationsComponent from "@UIParts/Notifications";
import ApplicationBottomBarComponent from "@UIParts/BarBottom";


import HomePageComponent from "@UIPages/Authenticated/HomePage";
import MonitoryNotesPageComponent from "@UIPages/Authenticated/MonitoryNotes";
import DispositionsPageComponent from "@UIPages/Authenticated/Dispositions";
import TerminalsPageComponent from "@UIPages/Authenticated/Terminals";
import SettingsPageComponent from "@UIPages/Authenticated/Settings";

import NotificationsPageComponent from "@UIPages/Authenticated/Notifications";


import HTTP_ERROR_RESPONSE_INTERFACE from "@Services/Interfaces/Http/Response/Error";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";

import NOTIFICATION_INTERFACE from "@Services/Application/Interfaces/Notifications/Notification";


import REST_API_REQUEST from "@Services/Utilities/Requests";

import prepareRestApiRequestToken from "@Services/Utilities/RequestToken/Prepare";


type AuthenticatedUserComponentProps = {};


export const AuthenticatedUserComponent: React.FC<AuthenticatedUserComponentProps> = (): JSX.Element | null => {


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state):ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /** const ROUTER {APPLICATION_ROUTER_INTERFACE} - Application Router Data Object */
    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    /** @const REST_API_URL {string} - Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;


    /** @const REST_API_DATA {object} - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const REST_API_REQUEST_TOKEN {AUTHORIZATION_TOKEN_INTERFACE} - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const [USER_NOTIFICATIONS, SET_USER_NOTIFICATIONS] = useState<NOTIFICATION_INTERFACE[]>([]);

    useEffect(() => {

        const NOTIFICATIONS_API_URL = `${REST_API_URL}/App/Notification/List`;

        const NOTIFICATIONS_REQUEST_DATA: any = {UserId: LOGGED_USER?.userID};

        /* Rest API User Notifications Request Handler */
        const GET_USER_NOTIFICATIONS = async (): Promise<void> => {

            /* Create REST API Request */
            try {

                /* User Notifications API Request */
                const NOTIFICATIONS_RESPONSE = await REST_API_REQUEST(
                    NOTIFICATIONS_API_URL, // API Request Endpoint URL
                    NOTIFICATIONS_REQUEST_DATA, // API Request Data
                    REST_API_REQUEST_TOKEN // API Request Actual AUTH TOKEN
                );

                /* Check Response Status */
                if (NOTIFICATIONS_RESPONSE.status === 200) { // Response OK
                    /* Read Response Body */
                    const NOTIFICATIONS_RESPONSE_TEXT: string = await NOTIFICATIONS_RESPONSE.text(); // Response Text
                    /* Check if Response Body is not empty */
                    if (NOTIFICATIONS_RESPONSE_TEXT.trim()) { // Response Body is not empty
                        /* Parse Response Text */
                        const NOTIFICATIONS: NOTIFICATION_INTERFACE[] = JSON.parse(NOTIFICATIONS_RESPONSE_TEXT)?.Notifications;
                        /* Remove Empty Notifications */
                        const NOTIFICATIONS_LIST = NOTIFICATIONS?.filter( // Filter Notifications
                            ((NOTIFICATION: any) => !!NOTIFICATION.Caption || !!NOTIFICATION.Text) // Remove Notifications without Data
                        )
                        /* Update Component State */
                        SET_USER_NOTIFICATIONS(NOTIFICATIONS_LIST); // Set Notifications List
                    }
                } else { // Defined Response Error
                    if (NOTIFICATIONS_RESPONSE.status === 409) {
                        /* Parse Error Response Content */
                        const ERROR_RESPONSE_CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await NOTIFICATIONS_RESPONSE.json();
                        /* Report Response Error */
                        console.log(NOTIFICATIONS_RESPONSE.status, ERROR_RESPONSE_CONTENT.UserMessage);
                    }
                }

            } catch (e) { // REST API Request Error
                /* Report Request Error */
                console.log(e);
            }

        };

        /* Trigger API Notifications List Request */
        GET_USER_NOTIFICATIONS(); // Run Async API Request

        return () => {
            SET_USER_NOTIFICATIONS([])
        };

    }, []);


    if (!LOGGED_USER?.userID) return <Redirect to={"/"} />;


    if (!ROUTER) return <Redirect to={"/404"} />;


    const LOGIN_PAGE_URL: string = ROUTER.NOT_AUTHENTICATED.LOGIN.URL;
    const HOME_PAGE_URL: string = ROUTER.AUTHENTICATED.HOME.URL;

    const MONITORY_NOTES_URL: string = ROUTER.AUTHENTICATED.MONITORY_NOTES.MONITORY_NOTES_LIST.URL;
    const DISPOSITIONS_URL: string = ROUTER.AUTHENTICATED.DISPOSITIONS.DISPOSITIONS_TERMINALS_LIST.URL;
    const TERMINALS_URL: string = ROUTER.AUTHENTICATED.PETROL_BASES.PETROL_BASES_LIST.URL;
    const SETTINGS_PAGE_URL: string = ROUTER.AUTHENTICATED.SETTINGS.MAIN_PAGE.URL;

    const NOTIFICATIONS_PAGE_URL: string = ROUTER.AUTHENTICATED.MESSAGES.MESSAGES.URL;


    return (
        <>

            <Route
                path={"/"} exact
                render={() =>
                    <ApplicationHeaderComponent />
                }
            />

            <Route
                path={"/"} render={() =>
                <ApplicationNotificationsComponent
                    USER_NOTIFICATIONS={USER_NOTIFICATIONS}
                />
                }
            />

            <Switch>

                <Route
                    path={HOME_PAGE_URL} exact
                    render={() =>
                        <HomePageComponent />
                    }
                />

                <Route
                    path={MONITORY_NOTES_URL}
                    render={() =>
                        <MonitoryNotesPageComponent />
                    }
                />

                <Route
                    path={DISPOSITIONS_URL}
                    render={() =>
                        <DispositionsPageComponent />
                    }
                />

                <Route
                    path={TERMINALS_URL}
                    render={() =>
                        <TerminalsPageComponent />
                    }
                />

                <Route
                    path={SETTINGS_PAGE_URL}
                    render={() =>
                        <SettingsPageComponent />
                    }
                />

                <Route
                    path={LOGIN_PAGE_URL} exact
                    render={() =>
                        <Redirect to={HOME_PAGE_URL} />
                    }
                />

                <Route
                    path={NOTIFICATIONS_PAGE_URL}
                    render={() =>
                        <NotificationsPageComponent NOTIFICATIONS={USER_NOTIFICATIONS} />
                    }
                />

                <Redirect to={HOME_PAGE_URL} />

            </Switch>

            <Route
                path={"/"}
                render={({location}) =>
                    <ApplicationBottomBarComponent location={location} />
                }
            />

        </>
    );

};


export default AuthenticatedUserComponent;
